/* global RELEASE_VERSION */
import { useEffect, useRef } from 'react'
import { environment } from 'js/config'
import { useLocation } from 'react-router-dom'

function useTracking() {
  const prevLocation = useRef()
  const location = useLocation()

  useEffect(() => {
    if (window.errorOutsideReact) {
      return loadTrackingScripts()
    }
    window.addEventListener('scroll', loadTrackingScripts, {
      once: true,
    })

    document.addEventListener('loadTracking', loadTrackingScripts, {
      once: true,
    })
    return () => {
      window.removeEventListener('scroll', loadTrackingScripts, { once: true })
      document.removeEventListener('loadTracking', loadTrackingScripts, {
        once: true,
      })
    }
  }, [])

  useEffect(() => {
    if (
      typeof prevLocation.current !== 'undefined' &&
      prevLocation.current.pathname !== location.pathname
    ) {
      loadTrackingScripts()
    }
    prevLocation.current = location
  }, [location.pathname])

  function addTrackers() {
    return Promise.all([
      import('js/trackers/mixpanel'),
      import('js/trackers/hotjar'),
      import('js/trackers/facebook'),
      import('js/trackers/optimove'),
      import('js/trackers/google'),
      new Promise(resolve => {
        if (environment !== 'development') {
          return import('@sentry/browser').then(Sentry => {
            resolve()
            window.sentryLoaded = true
            Sentry.init({
              beforeSend: (event, hint) => {
                if (
                  event.request &&
                  event.request.headers &&
                  event.request.headers['User-Agent'] &&
                  event.request.headers['User-Agent'].match(
                    'Mozilla/5.0 (Java) outbrain'
                  )
                ) {
                  return null
                }

                const error = hint.originalException
                if (!error || typeof error.message !== 'string') {
                  return event
                }
                if (
                  error.message.match(
                    /onAPWebViewPause|webkitExitFullScreen|navBarMenuCollapses|callbackSharePreferencesNotify|onAPWebViewResume|setAdGeneParamsForiOSApp|Can't find variable: iom|pktAnnotationHighlighter|cancelado|annullato|Abgebrochen|geannuleerd|cancelled|отменено|avbruten|avbrutt|annulleret|geannuleerd|annulé/
                  )
                ) {
                  return null
                }

                if (error.message.match(/^Loading chunk /)) {
                  event.fingerprint = [
                    'ChunkLoadError: Loading <chunkname> failed.',
                  ]
                  // ignore 99% of these errors since we have to many and since
                  // we aren't totally sure what causes them we keep 1% to be
                  // able to spot changes in the frequency of these etc
                  if (Math.floor(Math.random() * 100) !== 0) {
                    return null
                  }
                }

                // fix so that sentry can display the source code where the
                // error happened by faking that the error happened in the non
                // brotli version of the js file
                event.exception.values = event.exception.values.map(value => {
                  if (value.stacktrace && value.stacktrace.frames) {
                    value.stacktrace.frames = value.stacktrace.frames.map(
                      ({ filename, ...rest }) => ({
                        ...rest,
                        filename: filename.replace(/\.br$/, ''),
                      })
                    )
                  }
                  return value
                })

                return event
              },
              dsn: 'https://63c3a9e439904e03b5b944a6207f67e2@sentry.io/1440941',
              environment:
                window.location.host === 'lifesum.com'
                  ? 'production'
                  : 'staging',
              release: `lifesum-web@${RELEASE_VERSION}`,
            })

            window.sentryQueue.forEach(error => {
              if (Array.isArray(error)) {
                Sentry.captureException(error[4] || new Error(error[0]), {
                  extra: {
                    file: error[1],
                    line: error[2],
                    col: error[3],
                  },
                })
              } else {
                Sentry.withScope(scope => {
                  scope.setExtras(error.errorInfo)
                  Sentry.captureException(error.error)
                })
              }
            })
          })
        }
      }),
    ])
  }

  function loadTrackingScripts() {
    if (navigator.userAgent.match('DisableForTesting')) return
    setTimeout(() => {
      addTrackers().then(() => (window.TRACKING_LOADED = true))
    }, 1000)
  }
}

export default useTracking
